import * as React from 'react';
import styled from 'styled-components';
import { useInternalLink } from '@/hooks/useInternalLink';
import { useFetchBrowse } from '@/hooks/useFetchData';
import { useLinkFromId } from '@/helpers/link';
import { Link } from 'react-router-dom';
import consts from '@/config/consts';
import { useTranslation } from '../../hooks/useTranslation';

const pointSize = 40;

const HotspotPoint = styled.button`
  position: relative;
  width: ${pointSize}px;
  height: ${pointSize}px;
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.elvitaWebPurple};
    outline-offset: 2px;
  }

  &:hover {
    opacity: 0.9;
  }

  img {
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
`;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 9px 12px;
  color: ${({ theme }) => theme.colors.elvitaWebPurple};
  font-size: ${({ theme }) => theme.fontSizes.xxxs};
  line-height: 1.3;
  letter-spacing: 0.05em;
  border-radius: 6px;
  max-width: 200px;
  width: max-content;

  &:after {
    background: url(/images/arrow.svg);
    background-size: contain;
    content: ' ';
    width: 12px;
    height: 7px;
    background-repeat: no-repeat;
    margin-left: ${({ theme }) => theme.space.xs};
    flex-shrink: 0;
    background-position: right;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.elvitaWebPink};

    &:after {
      background: url(/images/arrow-active.svg);
      background-size: contain;
      content: ' ';
      background-repeat: no-repeat;
      background-position: right;
    }
  }
`;

const TextContent = styled.span`
  display: inline-block;
  max-width: 110px;
`;

const PopoverWrapper = styled.div<{ left: string; top: string; isOpen: boolean }>`
  position: absolute;
  left: ${({ left }) => `${parseFloat(left) * 100}%`};
  top: ${({ top }) => `${parseFloat(top) * 100}%`};
  transform: translate(-${pointSize / 2}px, -${pointSize / 2}px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  z-index: ${({ isOpen }) => (isOpen ? 100 : 1)};
`;

const HiddenLink = styled(Link) <{ isVisible: boolean }>`
  position: ${({ isVisible }) => (isVisible ? 'absolute' : 'absolute')};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
  left: ${({ isVisible }) => (isVisible ? '15px' : '-9999px')};
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: transparent;
  border: none;
  padding: 0;
  display: inline-block;
  cursor: pointer;
  max-width: 200px;
  border-radius: 2px;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.elvitaWebPurple};
    outline-offset: 2px;
    opacity: 1;
    pointer-events: auto;
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);
  }

  &:hover {
    opacity: 0.9;
  }
`;

type HotspotPopoverProps = {
	point: Elvita.Point;
	parentElement: HTMLElement;
	slideIndex?: number;
	swiperRef?: React.RefObject<any>;
};

const HotspotPopover: React.FC<HotspotPopoverProps> = ({
	point,
	parentElement,
	slideIndex,
	swiperRef,
}) => {
	const [opened, setOpened] = React.useState(false);
	const { internalLink } = useInternalLink();
	const { data } = useFetchBrowse();
	const idLink = useLinkFromId(data?.data, point.link);
	const { t } = useTranslation();
	const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

	const getLink = () => {
		const link =
			point.type === 'hotspot'
				? internalLink(`/hotspots/${point.link}`)
				: idLink;

		return link;
	};

	const handleOpen = () => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
		setOpened(true);
	};

	const handleFocus = () => {
		// Update swiper slide when tabbing to a hotspot on a different slide
		if (swiperRef?.current && slideIndex !== undefined) {
			const currentSlide = swiperRef.current.realIndex;
			if (currentSlide !== slideIndex) {
				// Set speed to match default swiper transition
				const swiper = swiperRef.current;
				const originalSpeed = swiper.params.speed;

				// Use default swiper speed or set a smooth one
				if (!originalSpeed || originalSpeed === 0) {
					swiper.params.speed = 300;
				}

				// Use slideTo with runCallbacks to trigger proper animations
				swiper.slideTo(slideIndex, swiper.params.speed, true);

				// Wait for transition to complete before opening popover
				setTimeout(() => {
					handleOpen();
				}, swiper.params.speed || 300);
				return;
			}
		}
		handleOpen();
	};

	const handleMouseLeave = () => {
		closeTimeoutRef.current = setTimeout(() => {
			setOpened(false);
		}, 150);
	};

	const handleBlur = () => {
		// For keyboard navigation, close immediately without delay
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
		setOpened(false);
	};

	React.useEffect(() => {
		return () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
			}
		};
	}, []);

	return (
		<PopoverWrapper left={point.x} top={point.y} isOpen={opened}>
			<HotspotPoint
				onClick={handleOpen}
				onMouseEnter={handleOpen}
				onMouseLeave={handleMouseLeave}
				onFocus={handleFocus}
				onBlur={handleBlur}
				aria-label={`${point.text}`}
				aria-expanded={opened ? 'true' : 'false'}
				type="button"
			>
				<img
					src={
						opened
							? '/images/hotspot-point-active.svg'
							: '/images/hotspot-point.svg'
					}
					alt=""
				/>
			</HotspotPoint>
			<HiddenLink
				className="hotspot-link"
				to={getLink()}
				isVisible={opened}
				onMouseEnter={handleOpen}
				onMouseLeave={handleMouseLeave}
				onFocus={handleFocus}
				onBlur={handleBlur}
				aria-label={`${point.text}`}
			>
				<PopoverContent>
					<TextContent>{point.text}</TextContent>
				</PopoverContent>
			</HiddenLink>
		</PopoverWrapper>
	);
};

const Wrapper = styled.div`
  position: relative;
`;

const Image = styled.img`
  display: inline-block;

  min-width: 300px;
  max-width: 300px;
  min-height: 300px;
  max-height: 300px;
`;

type HotspotImageProps = {
	image: Elvita.HotspotImage;
	slideIndex?: number;
	swiperRef?: React.RefObject<any>;
};

const HotspotImage: React.FC<HotspotImageProps> = ({ image, slideIndex, swiperRef }) => {
	const wrapperRef = React.useRef<HTMLDivElement>(null);
	const [loaded, setLoaded] = React.useState(false);

	React.useEffect(() => {
		if (wrapperRef?.current) {
			setLoaded(true);
		}
	}, []);

	return (
		<Wrapper ref={wrapperRef}>
			<Image src={`${consts.imagesCdnUrl}/images/${image.imageId}`} />
			{loaded &&
				image.points.map((point, index) => (
					<HotspotPopover
						parentElement={wrapperRef.current}
						point={point}
						slideIndex={slideIndex}
						swiperRef={swiperRef}
						key={index}
					/>
				))}
		</Wrapper>
	);
};

export default HotspotImage;
